import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { useId } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../../../lib/utils.js';

export interface CheckboxProps {
  /** Text label rendered next to the checkbox control. */
  label?: ReactNode;
  /** Helper or validation message shown below the checkbox label. */
  helperText?: string;
  /** Marks the control as invalid and applies error styles. */
  error?: boolean;
  /** Controlled checked state. */
  checked?: boolean | 'indeterminate';
  /** Uncontrolled checked state. */
  defaultChecked?: boolean | 'indeterminate';
  /** Called whenever checked value changes. */
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  /** Optional id for label association. */
  id?: string;
  /** Disables interaction. */
  disabled?: boolean;
  /** Required state. */
  required?: boolean;
  /** Extra classes for root wrapper. */
  className?: string;
  /** aria-describedby for external helper/error element IDs. */
  ariaDescribedBy?: string;
}

export function Checkbox({
  className,
  label,
  helperText,
  error = false,
  checked,
  defaultChecked,
  onCheckedChange,
  id,
  disabled = false,
  required = false,
  ariaDescribedBy,
}: CheckboxProps) {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;
  const helperId = helperText ? `${checkboxId}-helper` : undefined;
  const describedBy = [ariaDescribedBy, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <label
      className={cn('inline-flex cursor-pointer items-start gap-2 text-body-14 text-[var(--text-primary)]', className)}
      htmlFor={checkboxId}
    >
      <RadixCheckbox.Root
        id={checkboxId}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        required={required}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        className={cn(
          'mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-sm border bg-[var(--surface-input)] text-[var(--accent)]',
          error ? 'border-[var(--danger)]' : 'border-[var(--border-default)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ig-focus-ring-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ig-focus-ring-offset-color)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <RadixCheckbox.Indicator className="inline-flex items-center justify-center">
          {checked === 'indeterminate' ? <Minus size={12} aria-hidden /> : <Check size={12} aria-hidden />}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
      <span className="flex flex-col gap-1">
        {label && <span>{label}</span>}
        {helperText ? (
          <span id={helperId} className={cn('text-label-12', error ? 'text-[var(--danger)]' : 'text-[var(--text-secondary)]')}>
            {helperText}
          </span>
        ) : null}
      </span>
    </label>
  );
}
